/**
 * The SMS number's client-side gate.
 *
 * The server normalizes to E.164 and then REJECTS what it could not read
 * (`PhoneNormalizable`), so this predicate exists to say the same thing one round
 * trip earlier — at the field, while the dispatcher is still on the call. It must
 * accept exactly what the server accepts *after* normalization, no more and no
 * less, or the two would disagree about a number and the dispatcher would be
 * corrected by a 422 they can't see.
 *
 * Accepted: a ten-digit NANP number in any punctuation, the same with a leading 1,
 * or an already-international number. Refused: an extension or a note
 * (« 555-0123 poste 4 ») — real information, but not textable, so it belongs in
 * the CALL number. Blank is always fine: no cell means no texting, which is a
 * legitimate state and never an error.
 */
const NANP_DIGITS = 10

/**
 * ★ WHAT A HUMAN READS. `contact.rb` splits the two on purpose: `cell_phone`
 * reads the CANONICAL form « because a carrier accepts nothing else », `phone`
 * reads EXACTLY what the dispatcher typed « — it is read by a HUMAN ». But
 * `contactCallNumber` is `cell_phone || phone`, so every customer who gave a
 * cell hands the carrier's spelling to a call button: « +15815552001 » sitting
 * one row above « 581-555-0606 », two spellings of one fact on one screen.
 *
 * So display goes through here. A ten-digit NANP number (with or without its
 * leading 1) prints « (581) 555-2001 ». Everything else is returned UNTOUCHED:
 * an international number, an extension, a note — those are already the form
 * somebody chose, and re-punctuating what we cannot read with certainty is how
 * a formatter starts lying. Same conservative rule `PhoneNormalizable` applies
 * on the way in.
 *
 * ⚠ DISPLAY ONLY. Never feed this to `tel:`, to an SMS payload or to a filter:
 * those want the stored value.
 */
export const formatPhone = (raw: string | null | undefined): string => {
  const value = (raw ?? '').trim()
  if (!value) return ''
  // A value carrying anything but digits and phone punctuation is a note.
  if (/[^\d\s()+.-]/.test(value)) return value

  const digits = value.replace(/\D/g, '')
  const national =
    digits.length === NANP_DIGITS ? digits
      : digits.length === NANP_DIGITS + 1 && digits.startsWith('1') ? digits.slice(1)
        : null
  if (!national) return value
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
}

export const isTextablePhone = (raw: string | null | undefined): boolean => {
  const value = (raw ?? '').trim()
  if (!value) return true

  const digits = value.replace(/\D/g, '')
  // Anything with characters other than digits and phone punctuation is a note,
  // not a number — « poste », « ext », a name — and must not pass.
  if (/[^\d\s()+.-]/.test(value)) return false
  if (value.startsWith('+')) return /^\+[1-9]\d{7,14}$/.test(`+${digits}`)
  if (digits.length === NANP_DIGITS) return true
  return digits.length === NANP_DIGITS + 1 && digits.startsWith('1')
}
